/**
 * What the client's money has earned: the whole curve, and month by month.
 *
 * Earnings are the closed trades' result, measured apart from the client's own
 * transfers: a deposit is drawn on the curve as a mark, never as a rise. The
 * month's rate is the server's (closed results over the capital put in), so
 * this screen divides nothing.
 */
import { useQueries } from "@tanstack/react-query";
import type { EChartsCoreOption } from "echarts/core";
import { useEffect, useRef } from "react";

import { Panel } from "@/design-system/Panel";
import { ShareButton } from "@/shared/share/ShareButton";
import type { PeriodCard } from "@/shared/share/shareCard";
import { recentMonths } from "@/shared/period/months";
import type { AccountAnalyticsResponse } from "@/shared/api";
import {
  ALL_ACCOUNT_PERIOD,
  accountAnalyticsQuery,
  useAccountAnalytics,
} from "@/shared/analytics/useAccountAnalytics";
import { finiteGeometryValue } from "@/shared/money/geometry";
import { isNegative, isZero, money, signedMoney, signedPercent } from "@/shared/money/money";

type ChartHandle = {
  readonly setOption: (option: EChartsCoreOption, notMerge?: boolean) => void;
  readonly resize: () => void;
  readonly dispose: () => void;
};

function token(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export function earnedChartOption(analytics: AccountAnalyticsResponse): EChartsCoreOption {
  const points = analytics.realised
    .map((point) => [Date.parse(point.at), finiteGeometryValue(point.value)] as const)
    .filter(([at, value]) => Number.isFinite(at) && value !== null);
  const flows = analytics.flows.filter((flow) => !isZero(flow.amount));
  const muted = token("--color-text-muted");
  const positive = token("--color-chart-positive");
  return {
    animation: false,
    aria: { enabled: true, label: { description: "Заработано на закрытых сделках с начала работы" } },
    grid: { left: 8, right: 16, top: 24, bottom: 24, containLabel: true },
    tooltip: { trigger: "axis" },
    xAxis: { type: "time", axisLabel: { color: muted, hideOverlap: true, formatter: "{dd}.{MM}" }, splitLine: { show: false } },
    yAxis: {
      type: "value",
      axisLabel: { color: muted },
      splitLine: { lineStyle: { color: token("--color-chart-grid"), type: "dashed" } },
    },
    series: [
      {
        type: "line",
        name: "Заработано",
        showSymbol: false,
        data: points,
        lineStyle: { color: positive, width: 2.5 },
        itemStyle: { color: positive },
        areaStyle: { color: positive, opacity: 0.18 },
        markLine: {
          symbol: "none",
          silent: true,
          data: flows.map((flow) => ({
            xAxis: Date.parse(flow.at),
            lineStyle: { color: muted, type: "dashed", width: 1 },
            // Unlabelled: several transfers a week apart would print on top
            // of each other. The caption names the dashes; the amounts are on
            // the billing page.
            label: { show: false },
          })),
        },
      },
    ],
  };
}

function EarnedChart({ analytics }: { readonly analytics: AccountAnalyticsResponse }): React.JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ChartHandle | null>(null);
  const latest = useRef(analytics);
  latest.current = analytics;

  useEffect(() => {
    const host = hostRef.current;
    if (host === null || typeof ResizeObserver === "undefined") return undefined;
    let cancelled = false;
    let resize: ResizeObserver | null = null;
    void import("@/shared/analytics/chartEngine").then(({ initialiseChart }) => {
      if (cancelled) return;
      const chart = initialiseChart(host);
      chartRef.current = chart;
      chart.setOption(earnedChartOption(latest.current), true);
      resize = new ResizeObserver(() => {
        chart.resize();
      });
      resize.observe(host);
    });
    return () => {
      cancelled = true;
      resize?.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    chartRef.current?.setOption(earnedChartOption(analytics), true);
  }, [analytics]);

  return (
    <div
      ref={hostRef}
      className="h-56 w-full"
      role="img"
      aria-label="График: сколько заработано на закрытых сделках, с отметками пополнений"
      data-testid="client-earned-chart"
    />
  );
}

function periodCard(title: string, summary: AccountAnalyticsResponse["summary"]): PeriodCard {
  return {
    kind: "period",
    title,
    roi: summary.owner_return,
    pnl: summary.realised_net,
    deals: summary.cycles,
    winrate: summary.winrate,
    averageNet: summary.average_net,
  };
}

export function ClientReturns(): React.JSX.Element {
  const all = useAccountAnalytics(ALL_ACCOUNT_PERIOD);
  const months = recentMonths(new Date(), 6);
  const monthly = useQueries({
    queries: months.map((month) =>
      accountAnalyticsQuery({ since: month.since, until: month.until, preset: "custom" }),
    ),
  });
  const started = all.data?.coverage_since ?? null;
  const rows = months
    .map((month, index) => ({ month, result: monthly[index]?.data ?? null }))
    // A month that ended before the account traded is not a month of results.
    .filter(({ month }) => started === null || Date.parse(month.until) > Date.parse(started));

  return (
    <Panel
      title="Доходность"
      testId="client-returns"
      trailing={all.data === undefined || all.data.summary.cycles === 0 ? undefined : (
        <ShareButton card={periodCard("За всё время", all.data.summary)} name="skryptos-result" />
      )}
    >
      <div className="flex flex-col gap-4">
        {all.data === undefined ? (
          <p className="text-body-main text-text-secondary">
            {all.isError ? "График сейчас недоступен, обновлю через минуту." : "Считаю…"}
          </p>
        ) : (
          <div className="flex flex-col gap-1">
            <EarnedChart analytics={all.data} />
            <p className="text-helper-sm text-text-muted">
              Линия — сколько заработано закрытыми сделками с начала работы. Пунктир — ваши
              пополнения и выводы: они не входят в заработок.
            </p>
          </div>
        )}
        <table className="w-full text-body-main" data-testid="client-months">
          <caption className="sr-only">Доходность по месяцам</caption>
          <thead>
            <tr className="text-left text-helper-sm text-text-muted">
              <th className="py-1 font-normal">Месяц</th>
              <th className="py-1 text-right font-normal">Заработано</th>
              <th className="py-1 pl-3 text-right font-normal">%</th>
              <th className="py-1 pl-3 text-right font-normal"><span className="sr-only">Поделиться</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ month, result }) => (
              <tr key={month.since} className="border-t border-border-subtle">
                <td className="py-1.5">{month.label}</td>
                <td
                  className={`py-1.5 text-right tabular-nums ${
                    result !== null && isNegative(result.summary.realised_net)
                      ? "text-status-negative"
                      : "text-text-primary"
                  }`}
                >
                  {result === null ? "…" : signedMoney(result.summary.realised_net)}
                </td>
                <td className="py-1.5 pl-3 text-right tabular-nums text-text-secondary">
                  {result?.summary.owner_return == null ? "—" : signedPercent(result.summary.owner_return, 1)}
                </td>
                <td className="py-1 pl-3 text-right">
                  {result !== null && result.summary.cycles > 0 && (
                    <ShareButton card={periodCard(month.label, result.summary)} name={`skryptos-${month.since.slice(0, 7)}`} label="Поделиться месяцем" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {all.data !== undefined && (
          <p className="text-helper-sm text-text-muted">
            Суммы в USDT. Процент — заработок месяца к вложенным {money(all.data.summary.invested)} USDT.
            Текущий месяц ещё идёт.
          </p>
        )}
      </div>
    </Panel>
  );
}
